/**
 * One way to write stakes in the lobby: "$1.00 / $2.00" — a space either side
 * of the slash (owner, 2026-09-08: readability). The engine sends "$1.00/$2.00"
 * and the adapters used to pass it through; every lobby surface that shows a
 * stakes string (cash tiles, the stake headings the tiles are grouped under,
 * recent tables) reads the label this produces. Anything without a slash
 * ("Unknown") passes through untouched.
 */
export const formatStakesLabel = (label: string): string => {
  // The engine payloads are untyped JSON and this is the boundary they cross:
  // `useActiveTables` reads `stakes` straight off a socket slice, so a payload
  // whose shape drifts hands us a number and `.includes` throws — taking the
  // whole active-tables hook, and the lobby's Current Tables panel, with it
  // (hijack-review, 2026-09-14). Guarded here rather than at each call site so
  // every reader of the label gets it.
  if (typeof label !== 'string') return '';
  if (!label.includes('/')) return label;
  return label
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' / ');
};

/**
 * Prize-pool figure for a Sit & Go heading, e.g. 30 → "$30", 12.5 → "$12.50".
 *
 * One locale call for both cases (JRam, 2026-09-14). It used to branch to
 * `toFixed(2)` for non-integers, which drops the thousands separator the
 * integer branch adds — so a $1,250 pool and a $1,250.50 pool sat in the same
 * column punctuated differently, "$1,250" beside "$1250.50". EN-10743 makes
 * this figure the Sit & Go tab's entire heading, so it is worth closing here.
 *
 * `en-US` and not the browser's locale (hijack-review, 2026-09-14): the figure
 * carries a hard-coded `$`, so taking the reader's grouping and decimal marks
 * would print `$1.250,50` to a de-DE player — a dollar sign punctuated as
 * euros. The old integer branch had the same flaw via a bare toLocaleString().
 */
export const formatPrize = (n: number): string =>
  `$${n.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2
  })}`;
